import { isTokenExpired, isTokenExpiring } from "@/http/jwt"
import { env } from "@/lib/env"
import { secureStorage } from "@/lib/secure-storage"

const apiURL = env.apiUrl

// Controls a single in-flight refresh to prevent stampedes
let refreshInFlight: Promise<string | null> | null = null

// Simple in-memory cache for session checks to avoid repeated network calls during preload
let ensureSessionCache: Promise<boolean> | null = null

export function clearSessionCache() {
  ensureSessionCache = null
  refreshInFlight = null
}

export function hasValidAccessToken(): boolean {
  const { access } = secureStorage.getTokens()
  return Boolean(access && !isTokenExpired(access))
}

export function hasValidRefreshToken(): boolean {
  const { refresh } = secureStorage.getTokens()
  return Boolean(refresh && !isTokenExpired(refresh))
}

export function hasActiveSession(): boolean {
  return hasValidAccessToken() || hasValidRefreshToken()
}

async function performTokenRefresh(refresh: string): Promise<string> {
  const url = `${apiURL.replace(/\/$/, "")}/users/sessions/refresh`
  const response = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include", // Important: send cookies for server-side httpOnly support
    body: JSON.stringify({ refreshToken: refresh }),
  })

  if (!response.ok) {
    throw response
  }

  const data = (await response.json()) as {
    accessToken: string
    refreshToken?: string
  }
  if (!data?.accessToken) {
    throw new Error("Invalid refresh response")
  }

  // Store the new tokens (including refresh if provided)
  secureStorage.setTokens(data.accessToken, data.refreshToken)

  return data.accessToken
}

export async function ensureFreshAccessToken(
  force = false,
): Promise<string | null> {
  const tokens = secureStorage.getTokens()

  const needsRefresh = force || !tokens.access || isTokenExpiring(tokens.access)
  if (!needsRefresh) return tokens.access ?? null

  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      try {
        const currentTokens = secureStorage.getTokens()
        if (!currentTokens?.refresh || isTokenExpired(currentTokens.refresh)) {
          // Session is definitely dead
          const { clearAllAppState } = await import("@/http/clear-app-state")
          clearAllAppState()
          return null
        }
        return await performTokenRefresh(currentTokens.refresh)
      } catch (error) {
        console.error("Failed to refresh access token", error)

        // Only clear state if it's a definitive auth error
        if (
          error instanceof Response &&
          (error.status === 400 || error.status === 401 || error.status === 403)
        ) {
          try {
            const { clearAllAppState } = await import("@/http/clear-app-state")
            clearAllAppState()
          } catch {}
          return null
        }

        // For network/server errors, don't log out the user.
        // If we still have a valid access token and this wasn't a forced refresh,
        // allow callers to proceed with the existing token.
        const fallbackTokens = secureStorage.getTokens()
        if (
          !force &&
          fallbackTokens.access &&
          !isTokenExpired(fallbackTokens.access)
        ) {
          return fallbackTokens.access
        }

        if (!force) {
          return null
        }

        // Forced refresh should propagate errors so callers can decide how to handle them.
        throw error
      } finally {
        // Allow next refresh attempts
        refreshInFlight = null
      }
    })()
  }

  return refreshInFlight
}

export async function ensureSession(): Promise<boolean> {
  if (!ensureSessionCache) {
    const sessionPromise = (async () => {
      const tokens = secureStorage.getTokens()
      if (tokens.access && !isTokenExpired(tokens.access)) return true
      if (!tokens.refresh || isTokenExpired(tokens.refresh)) return false

      try {
        const refreshed = await ensureFreshAccessToken(true)
        if (refreshed) return true
      } catch {
        // Forced refresh can throw errors
        // Fall through to check if we still have a valid refresh token
      }

      const { refresh } = secureStorage.getTokens()
      return Boolean(refresh && !isTokenExpired(refresh))
    })()

    ensureSessionCache = sessionPromise.catch((error) => {
      ensureSessionCache = null
      throw error
    })
  }

  return ensureSessionCache
}
